import { FeatureFlag } from '@ps/web-core-enums';
import type {
  AvailableCashoutQuote,
  CashoutQuote,
  PendingCashoutQuote,
  SuspendedCashoutQuote
} from '@ps/web-sports-api-types';
import { useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import useInterval from '@swf/sports/hooks/useInterval';

import { useSportsMyBetsWidget } from '../model';

const getFetchableQuotes = (cashOutQuotes: Record<string, CashoutQuote>) =>
  Object.values(cashOutQuotes).filter(
    (quote): quote is AvailableCashoutQuote | SuspendedCashoutQuote | PendingCashoutQuote =>
      'refreshRate' in quote
  );

const useCashOutPoller = (isSettled = false) => {
  const { widgetSelect, widgetDispatch } = useSportsMyBetsWidget();
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const cashOutQuotes = useSelector(widgetSelect.cashoutQuotes);
  const isCashOutEnabled = useFeature(FeatureFlag.ISPCashOut);

  const fetchableCashoutQuotes = getFetchableQuotes(cashOutQuotes);
  const lowestRefreshRate = fetchableCashoutQuotes.reduce(
    (min, quote) => Math.min(min, quote.refreshRate),
    Infinity
  );
  const lowestRefreshRateInMs = lowestRefreshRate * 1000;

  const getCashoutQuotes = () => {
    if (!isCashOutEnabled || isSettled || isSelfExcluded || !fetchableCashoutQuotes.length) {
      return;
    }

    widgetDispatch.cashoutQuotesRequested();
  };

  useInterval(getCashoutQuotes, lowestRefreshRateInMs, lowestRefreshRateInMs);
};

export default useCashOutPoller;
